import { Box, HStack, IconButton, Stack, Text, Tooltip, Button, Alert, AlertIcon } from "@chakra-ui/react";
import { FunctionComponent, ReactElement, useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { ClaimServiceProvidedFor, UserRole } from "../../../utils/consts";
import {
	documentCategoryTypeToLocal,
	handleDocClick,
	validateFileSize,
	validateClaimSize,
	formatFileSize
} from "../../../utils/helpers";
import { FILE_SIZE_LIMITS } from "../../../utils/consts";
import { useAuth } from "../../../hooks/useAuth";
import { TiDelete } from "react-icons/ti";
import useClaimsToast from "../../../hooks/useToast";
import { DocumentCategory } from "../../../utils/api/protos/messages/claims.message_pb";
import StepperFooter from "../../../shared/StickyFooter/footers/StepperFooter";
import { MdInfoOutline, MdRefresh } from "react-icons/md";
import { generateCcsPdf } from "../../../utils/api/pdf";
import { RpcError } from "grpc-web";
import { handleError } from "../../../utils/provider/claims-provider-util";

// Components
import { SecondaryButton } from "../../../shared/Button/index.styles";
import FileOperationProgress, { FileOperationItem } from "../../../shared/FileOperationProgress";
import LoadingSpinner from "../../../shared/LoadingSpinner/LoadingSpinner";

import { DocLink } from "../NewClaim.styles";
// Context
import { useClaim } from "../../../context-providers/claims-provider";

// Protos
import { ClaimDocument } from "../../../utils/types";

interface AssociateDocsProps {
	/**
	 * Delegate to handle logic when User clicks the Previous button
	 * @returns void
	 */
	handleStepPrevious: () => void;
	handleStepClick: (index: number) => void;
	currentStep: number;
	hasGeneralAdminAccess: boolean;
}

const AssociateDocs: FunctionComponent<AssociateDocsProps> = (props: AssociateDocsProps): ReactElement => {
	const { handleStepPrevious, handleStepClick, currentStep, hasGeneralAdminAccess } = props;

	const {
		claim,
		handleDocumentAssociation,
		handleDocumentDisassociation,
		handlePermanentlyRemoveDocument,
		setIsLoading,
		isLoading,
		localBenefits,
		setClaim,
		setLocalClaim,
		ccsGenerationStatus,
		ccsGenerationError,
		setCcsGenerationStatus
	} = useClaim();

	// PDF generation state tracking (for manual generation on this page)
	const [pdfGenerationStatus, setPdfGenerationStatus] = useState<"idle" | "generating" | "success" | "failed">("idle");
	const [pdfGenerationError, setPdfGenerationError] = useState<string | null>(null);

	// Enhanced state for tracking file uploads and downloads with better progress management
	const [uploadOperations, setUploadOperations] = useState<FileOperationItem[]>([]);
	const [downloadOperations, setDownloadOperations] = useState<FileOperationItem[]>([]);

	// Derive CCS doc presence directly from claim state on every render
	const hasCcsDocs = claim.documents.some(
		(doc) => doc.category === documentCategoryTypeToLocal(DocumentCategory.CCS) && !doc.isDeleted
	);

	// Check if CCS generation is in progress (either from Billing step or manual generation here)
	const isGeneratingCcs = ccsGenerationStatus === "loading" || pdfGenerationStatus === "generating";
	const showProofOfSchool = claim.claimServices.find(
		(s) => s.serviceProvidedFor === ClaimServiceProvidedFor.DependentChild
	);
	const isSupportingDocsMandatory = localBenefits.some((benefit) => benefit.requireSupportingDocument);
	const isHeldForAudit = claim.isHeldForAudit === true;
	const hasAuditDocuments = claim.documents.some(
		(doc) => doc.category === documentCategoryTypeToLocal(DocumentCategory.AUDIT_DOCUMENTS) && !doc.isDeleted
	);
	if (isSupportingDocsMandatory) {
		console.log("[AssociateDocs] Supporting Documents are mandatory based on selected benefits.");
	} else {
		console.log("[AssociateDocs] Supporting Documents are optional based on selected benefits.");
	}
	const toast = useClaimsToast();
	const { getUser } = useAuth();
	const currentUser = getUser();

	// Function to generate or regenerate CCS PDF
	const handleGenerateCcsPdf = async () => {
		if (!claim?.claimId) {
			toast("Error", "Claim ID is missing", "error");
			return;
		}

		setPdfGenerationStatus("generating");
		setPdfGenerationError(null);

		try {
			const response = await generateCcsPdf(claim.claimId);

			// Extract file info from response
			const pdfUrl = response.getUrl();
			const fileId = response.getFileId();
			const fileName = response.getFilename();
			const fileSize = response.getSizeBytes();

			if (pdfUrl && fileId && fileName) {
				// Create the CCS document objects
				const ccsDocuments: ClaimDocument[] = [
					{
						file: {
							fileId: fileId,
							fileName: fileName,
							fileSize: fileSize
						},
						category: documentCategoryTypeToLocal(DocumentCategory.CCS),
						isDeleted: false,
						fileSize: fileSize,
						isGenerated: true
					}
				];

				// Add any additional PDFs if they exist
				const additionalPdfs = response.getAdditionalPdfsList();
				if (additionalPdfs && additionalPdfs.length > 0) {
					additionalPdfs.forEach((pdf) => {
						ccsDocuments.push({
							file: {
								fileId: pdf.getFileId(),
								fileName: pdf.getFilename(),
								fileSize: pdf.getSizeBytes()
							},
							category: documentCategoryTypeToLocal(DocumentCategory.CCS),
							isDeleted: false,
							fileSize: pdf.getSizeBytes(),
							isGenerated: true
						});
					});
				}

				// Remove existing auto-generated CCS documents and add the new ones
				const localCcsCategory = documentCategoryTypeToLocal(DocumentCategory.CCS);
				setClaim((prevClaim) => ({
					...prevClaim,
					documents: [
						...(prevClaim.documents || []).filter(
							(doc) => !(doc.category === localCcsCategory && doc.isGenerated === true)
						),
						...ccsDocuments
					]
				}));
				setLocalClaim((prevClaim) => ({
					...prevClaim,
					documents: [
						...(prevClaim.documents || []).filter(
							(doc) => !(doc.category === localCcsCategory && doc.isGenerated === true)
						),
						...ccsDocuments
					]
				}));

				const pdfCount = response.getTotalPdfCount() || ccsDocuments.length;
				toast("Success", `${pdfCount} claim form${pdfCount > 1 ? "s" : ""} generated successfully`, "success");
				setPdfGenerationStatus("success");
			} else {
				toast("Warning", "PDF generation completed but file info is incomplete", "warning");
				setPdfGenerationStatus("failed");
				setPdfGenerationError("File info incomplete");
			}
		} catch (err) {
			const error = err as RpcError;
			setPdfGenerationStatus("failed");
			setPdfGenerationError(error.message || "Unknown error occurred");
			handleError(toast, error, "Unable to generate PDF", "Error generating PDF");
		}
	};

	// Function to permanently remove a document in the claims intake flow
	const handlePermanentDocumentRemoval = (fileId: string) => {
		if (claim.claimId) {
			handlePermanentlyRemoveDocument(claim.claimId, fileId);
		} else {
			toast("Error", "Claim ID is missing", "error");
		}
	};

	// Enhanced download handler with progress tracking and loading screen
	const handleEnhancedDocClick = useCallback(
		async (fileId: string, fileName: string) => {
			// Prevent multiple downloads
			if (isLoading) return;

			setIsLoading(true);

			// Create a download operation entry
			const operationId = `${fileId}-${Date.now()}-${Math.random()}`;
			const newOperation: FileOperationItem = {
				id: operationId,
				fileName,
				progress: 0,
				status: "processing"
			};

			setDownloadOperations((prev) => [...prev, newOperation]);

			try {
				// Call handleDocClick with progress callback
				await handleDocClick(
					fileId,
					toast,
					true, // openNewTab
					undefined, // downloadFileName
					(progress: number) => {
						// Update progress
						setDownloadOperations((prev) => prev.map((op) => (op.id === operationId ? { ...op, progress } : op)));
					}
				);

				// Mark as completed
				setDownloadOperations((prev) =>
					prev.map((op) => (op.id === operationId ? { ...op, status: "completed" as const, progress: 100 } : op))
				);

				// Remove completed operation after a delay
				setTimeout(() => {
					setDownloadOperations((prev) => prev.filter((op) => op.id !== operationId));
				}, 3000);
			} catch (error) {
				console.error("Error downloading file:", error);
				const errorMessage = error instanceof Error ? error.message : "Download failed";

				// Mark as failed
				setDownloadOperations((prev) =>
					prev.map((op) => (op.id === operationId ? { ...op, status: "failed" as const, error: errorMessage } : op))
				);
			} finally {
				setIsLoading(false);
			}
		},
		[toast, setIsLoading]
	);

	const useOnDrop = (category: DocumentCategory) => {
		return useCallback(
			(acceptedFiles: Array<File>) => {
				if (acceptedFiles.length === 0) return;

				if (isHeldForAudit && category !== DocumentCategory.AUDIT_DOCUMENTS) {
					toast("Error", "Only audit documents can be uploaded while this claim is held for audit", "error");
					return;
				}

				// Validate file sizes before processing
				for (const file of acceptedFiles) {
					const fileValidation = validateFileSize(file.size, file.name);
					if (!fileValidation.isValid) {
						toast("File Size Error", fileValidation.error!, "error");
						return;
					}
				}

				// Validate total claim size
				const totalNewSize = acceptedFiles.reduce((sum, file) => sum + file.size, 0);
				const claimValidation = validateClaimSize(claim.documents, totalNewSize);
				if (!claimValidation.isValid) {
					toast("Claim Size Error", claimValidation.error!, "error");
					return;
				}

				setIsLoading(true);

				// Create initial upload operations for all files
				const newOperations: FileOperationItem[] = acceptedFiles.map((file) => ({
					id: `${file.name}-${Date.now()}-${Math.random()}`,
					fileName: file.name,
					progress: 0,
					status: "queued"
				}));

				setUploadOperations((prev) => [...prev, ...newOperations]);

				// Process each file
				acceptedFiles.forEach(async (file: File, index: number) => {
					const operationId = newOperations[index].id;

					try {
						// Update status to processing
						setUploadOperations((prev) =>
							prev.map((op) => (op.id === operationId ? { ...op, status: "processing" as const } : op))
						);

						// Upload the file and track progress
						await handleDocumentAssociation(null, file, category, (progress: number) => {
							setUploadOperations((prev) => prev.map((op) => (op.id === operationId ? { ...op, progress } : op)));
						});

						// Log after successful upload
						console.log(`[AssociateDocs] Successfully uploaded file "${file.name}" with category:`, category);

						// Mark as completed
						setUploadOperations((prev) =>
							prev.map((op) => (op.id === operationId ? { ...op, status: "completed" as const, progress: 100 } : op))
						);

						// Remove completed operation after a delay
						setTimeout(() => {
							setUploadOperations((prev) => prev.filter((op) => op.id !== operationId));
						}, 3000);
					} catch (error) {
						console.error("Error uploading file:", error);
						const errorMessage = error instanceof Error ? error.message : "Upload failed";

						// Mark as failed
						setUploadOperations((prev) =>
							prev.map((op) => (op.id === operationId ? { ...op, status: "failed" as const, error: errorMessage } : op))
						);

						toast("Error", `Failed to upload ${file.name}: ${errorMessage}`, "error");
					}
				});

				// Check if all uploads are complete
				setTimeout(() => {
					setUploadOperations((prev) => {
						const remaining = prev.filter((op) => op.status === "processing" || op.status === "queued");
						if (remaining.length === 0) {
							setIsLoading(false);
						}
						return prev;
					});
				}, 1000);
			},
			[claim.documents, handleDocumentAssociation, isHeldForAudit, toast]
		);
	};

	const {
		getRootProps: getRootPropsInvoices,
		getInputProps: getInputPropsInvoices,
		isDragActive: isDragActiveInvoices,
		open: openInvoices
	} = useDropzone({
		onDrop: useOnDrop(DocumentCategory.INVOICES),
		multiple: true,
		noClick: true,
		noKeyboard: true
	});

	const {
		getRootProps: getRootPropsSupportingDocuments,
		getInputProps: getInputPropsSupportingDocuments,
		isDragActive: isDragActiveSupportingDocuments,
		open: openSupportingDocuments
	} = useDropzone({
		onDrop: useOnDrop(DocumentCategory.SUPPORTING_DOCUMENTS),
		multiple: true,
		noClick: true,
		noKeyboard: true
	});

	const {
		getRootProps: getRootPropsClaimForms,
		getInputProps: getInputPropsClaimForms,
		isDragActive: isDragActiveClaimForms,
		open: openClaimForms
	} = useDropzone({
		onDrop: useOnDrop(3 as DocumentCategory), // Use numeric value 3 directly since DocumentCategory.CLAIM_FORMS has a syntax error in protobuf
		multiple: true,
		noClick: true,
		noKeyboard: true
	});

	const {
		getRootProps: getRootPropsAuditDocuments,
		getInputProps: getInputPropsAuditDocuments,
		isDragActive: isDragActiveAuditDocuments,
		open: openAuditDocuments
	} = useDropzone({
		onDrop: useOnDrop(DocumentCategory.AUDIT_DOCUMENTS),
		multiple: true,
		noClick: true,
		noKeyboard: true
	});

	const {
		getRootProps: getRootPropsProofOfFullTimeSchool,
		getInputProps: getInputPropsProofOfFullTimeSchool,
		isDragActive: isDragActiveProofOfFullTimeSchool,
		open: openProofOfFullTimeSchool
	} = useDropzone({
		onDrop: useOnDrop(DocumentCategory.PROOF_OF_FULL_TIME_SCHOOL_ENROLLMENT),
		multiple: true,
		noClick: true,
		noKeyboard: true
	});

	const handleNextClick = async () => {
		handleStepClick(currentStep + 1);
	};

	const isNextDisabled = (): boolean => {
		// Claim Forms (includes CCS) is ALWAYS required (even for admins)
		const localCcsCategory = documentCategoryTypeToLocal(DocumentCategory.CCS);
		const localClaimFormsCategory = documentCategoryTypeToLocal(DocumentCategory.CLAIM_FORMS);
		const hasClaimFormsOrCcs = claim.documents.find(
			(doc) => (doc.category === localCcsCategory || doc.category === localClaimFormsCategory) && !doc.isDeleted
		);

		if (!hasClaimFormsOrCcs) {
			// Next button disabled: Missing Claim Forms
			return true;
		}

		// For non-admins, also require Invoices
		if (!hasGeneralAdminAccess) {
			let checkCategories = [DocumentCategory.INVOICES];

			// Only add supporting documents to required categories if they are mandatory
			if (isSupportingDocsMandatory) {
				checkCategories.push(DocumentCategory.SUPPORTING_DOCUMENTS);
			}

			const categoriesFilled = checkCategories.filter((category) => {
				const localCategory = documentCategoryTypeToLocal(category);
				const hasDoc = claim.documents.find((doc) => doc.category === localCategory && !doc.isDeleted);
				return !!hasDoc;
			});

			const isDisabled = categoriesFilled.length !== checkCategories.length;
			return isDisabled;
		}

		return false;
	};

	const renderDocuments = (documents: Array<ClaimDocument>, category: DocumentCategory): ReactElement => {
		return (
			<Stack>
				<Box key={`document-wrapper`} maxWidth="100%" width="100%">
					<ul style={{ listStyle: "none" }}>
						{documents.map((doc: ClaimDocument) => {
							const localCategory = documentCategoryTypeToLocal(category);
							if (doc.category === localCategory) {
								// Check if user is a Provider (not Claims Analyst or Claims Manager)
								const isProvider = currentUser?.role.includes(UserRole.Provider) ?? false;
								const isClaimsAnalystOrManager =
									currentUser?.role.includes(UserRole.ClaimsAnalyst) ||
									currentUser?.role.includes(UserRole.ClaimsManager) ||
									currentUser?.role.includes(UserRole.ClaimsAdmin);

								// Providers cannot delete auto-generated CCS forms
								const isAutoGeneratedCcsRestricted =
									localCategory === "Ccs" && doc.isGenerated === true && (isProvider || isClaimsAnalystOrManager);

								const deleteTooltip = isAutoGeneratedCcsRestricted
									? "Providers cannot delete auto-generated Claim forms"
									: "Permanently delete document";

								return (
									<Box key={`document-${doc.file.fileId}`} maxWidth="100%" width="100%" display="flex">
										<li>
											<DocLink onClick={() => handleEnhancedDocClick(doc.file.fileId, doc.file.fileName)}>
												{doc.file.fileName}
											</DocLink>
											{!isAutoGeneratedCcsRestricted && (
												<IconButton
													colorScheme="blackAlpha"
													variant="ghost"
													verticalAlign="bottom"
													size="xs"
													ml={1}
													fontSize="1.5rem"
													aria-label="Remove Document"
													onClick={() => handlePermanentDocumentRemoval(doc.file.fileId)}
													icon={<TiDelete />}
													title={deleteTooltip}
													isDisabled={false}
													opacity={1}
												/>
											)}
										</li>
									</Box>
								);
							}
						})}
					</ul>
				</Box>
			</Stack>
		);
	};

	return (
		<>
			{/* Loading Screens */}
			{isLoading && <LoadingSpinner />}

			{/* Enhanced Upload Progress Display */}
			<FileOperationProgress operations={uploadOperations} operationType="upload" />

			{/* Enhanced Download Progress Display */}
			<FileOperationProgress operations={downloadOperations} operationType="download" />

			<Stack pb={8} rowGap={5}>
				<div>
					<Text as="span" fontSize="16" fontWeight={"700"} lineHeight={10}>
						Invoices{!hasGeneralAdminAccess && " *"}
					</Text>
					<Box
						{...getRootPropsInvoices()}
						backgroundColor={isDragActiveInvoices ? "gray.900" : "offWhite"}
						color="gray.1700"
						padding="16px"
						borderColor={isDragActiveInvoices ? "gray.800" : "gray.400"}
						borderWidth="1px"
						borderRadius="5px"
						borderStyle="dashed"
						display="flex">
						<Stack rowGap={4} width="100%">
							{renderDocuments(claim.documents, DocumentCategory.INVOICES)}
							<input {...getInputPropsInvoices()} />
							<HStack>
								<SecondaryButton
									colorScheme="blackAlpha"
									size={"sm"}
									onClick={openInvoices}
									isDisabled={isHeldForAudit}>
									Upload Files
								</SecondaryButton>
								<Text color={isDragActiveInvoices ? "orange.600" : "gray"}>or drag & drop</Text>
							</HStack>
							<Text fontSize="xs" color="gray.500" mt={2}>
								Max file size: {formatFileSize(FILE_SIZE_LIMITS.MAX_FILE_SIZE)} | Max claim size:{" "}
								{formatFileSize(FILE_SIZE_LIMITS.MAX_CLAIM_SIZE)}
							</Text>
						</Stack>
					</Box>
				</div>
				<div>
					<Text as="span" fontSize="16" fontWeight="700" lineHeight={10}>
						Supporting Documents
						{isSupportingDocsMandatory && !hasGeneralAdminAccess && " *"}
						{!isSupportingDocsMandatory && (
							<Text as="span" color="black" fontSize="xs" ml={1} fontWeight="500">
								(Optional)
							</Text>
						)}
					</Text>
					<Box
						{...getRootPropsSupportingDocuments()}
						backgroundColor={isDragActiveSupportingDocuments ? "gray.900" : "offWhite"}
						color="gray.1700"
						padding="16px"
						borderColor={isDragActiveSupportingDocuments ? "gray.800" : "gray.400"}
						borderWidth="1px"
						borderRadius="5px"
						borderStyle="dashed"
						display="flex">
						<Stack rowGap={4} width="100%">
							{renderDocuments(claim.documents, DocumentCategory.SUPPORTING_DOCUMENTS)}
							<input {...getInputPropsSupportingDocuments()} />
							<HStack>
								<SecondaryButton
									colorScheme="blackAlpha"
									size={"sm"}
									onClick={openSupportingDocuments}
									isDisabled={isHeldForAudit}>
									Upload Files
								</SecondaryButton>
								<Text color={isDragActiveSupportingDocuments ? "orange.600" : "gray"}>or drag & drop</Text>
							</HStack>
							<Text fontSize="xs" color="gray.500" mt={2}>
								Max file size: {formatFileSize(FILE_SIZE_LIMITS.MAX_FILE_SIZE)} | Max claim size:{" "}
								{formatFileSize(FILE_SIZE_LIMITS.MAX_CLAIM_SIZE)}
							</Text>
						</Stack>
					</Box>
				</div>
				<div>
					<Text as="span" fontSize="16" fontWeight={"700"} lineHeight={10}>
						Claim Forms{!hasGeneralAdminAccess && " *"}
					</Text>
					<Text fontSize="xs" color="gray.600" mt={1} mb={2}>
						Upload claim forms or generate Claim forms automatically
					</Text>
					<Box
						{...getRootPropsClaimForms()}
						backgroundColor={isDragActiveClaimForms ? "gray.900" : "offWhite"}
						color="gray.1700"
						padding="16px"
						borderColor={isDragActiveClaimForms ? "gray.800" : "gray.400"}
						borderWidth="1px"
						borderRadius="5px"
						borderStyle="dashed"
						display="flex">
						<Stack rowGap={4} width="100%">
							{renderDocuments(claim.documents, DocumentCategory.CLAIM_FORMS)}
							{renderDocuments(claim.documents, DocumentCategory.CCS)}

							{/* Show status messages based on PDF generation state */}
							{!hasCcsDocs && (
								<>
									{isGeneratingCcs && (
										<Alert status="info" borderRadius="md">
											<AlertIcon />
											<Box flex="1">
												<Text fontSize="sm" fontWeight="medium">
													Generating Claim forms...
												</Text>
												<Text fontSize="xs" color="gray.600" mt={1}>
													This may take a few moments. The forms will appear automatically once ready.
												</Text>
											</Box>
										</Alert>
									)}

									{!isGeneratingCcs && (pdfGenerationStatus === "failed" || ccsGenerationStatus === "error") && (
										<Alert status="error" borderRadius="md">
											<AlertIcon />
											<Box flex="1">
												<Text fontSize="sm" fontWeight="medium">
													Claim form generation failed
												</Text>
												<Text fontSize="xs" color="gray.600" mt={1}>
													{pdfGenerationError ||
														ccsGenerationError ||
														"An error occurred while generating the PDF. Please try again."}
												</Text>
												<Button
													size="sm"
													colorScheme="red"
													variant="outline"
													mt={2}
													leftIcon={<MdRefresh />}
													onClick={handleGenerateCcsPdf}>
													Retry Generation
												</Button>
											</Box>
										</Alert>
									)}

									{!isGeneratingCcs && pdfGenerationStatus !== "failed" && ccsGenerationStatus !== "error" && (
										<Alert status="warning" borderRadius="md">
											<AlertIcon />
											<Box flex="1">
												<Text fontSize="sm" fontWeight="medium">
													No Claim forms found
												</Text>
												<Text fontSize="xs" color="gray.600" mt={1}>
													Claim forms should have been generated automatically. Click below to generate them now.
												</Text>
												<Button
													size="sm"
													colorScheme="orange"
													variant="outline"
													mt={2}
													leftIcon={<MdRefresh />}
													onClick={handleGenerateCcsPdf}>
													Generate Claim Forms
												</Button>
											</Box>
										</Alert>
									)}
								</>
							)}

							<input {...getInputPropsClaimForms()} />
							<HStack>
								<SecondaryButton
									colorScheme="blackAlpha"
									size={"sm"}
									onClick={openClaimForms}
									isDisabled={isHeldForAudit}>
									Upload Files
								</SecondaryButton>
								<Text color={isDragActiveClaimForms ? "orange.600" : "gray"}>or drag & drop</Text>
							</HStack>
							<Text fontSize="xs" color="gray.500" mt={2}>
								Max file size: {formatFileSize(FILE_SIZE_LIMITS.MAX_FILE_SIZE)} | Max claim size:{" "}
								{formatFileSize(FILE_SIZE_LIMITS.MAX_CLAIM_SIZE)}
							</Text>
						</Stack>
					</Box>
				</div>
				{(isHeldForAudit || hasAuditDocuments) && (
					<div>
						<Text as="span" fontSize="16" fontWeight={"700"} lineHeight={10}>
							Audit Documents
						</Text>
						<Box
							{...getRootPropsAuditDocuments()}
							backgroundColor={isDragActiveAuditDocuments ? "gray.900" : "offWhite"}
							color="gray.1700"
							padding="16px"
							borderColor={isDragActiveAuditDocuments ? "gray.800" : "gray.400"}
							borderWidth="1px"
							borderRadius="5px"
							borderStyle="dashed"
							display="flex">
							<Stack rowGap={4} width="100%">
								{renderDocuments(claim.documents, DocumentCategory.AUDIT_DOCUMENTS)}
								<input {...getInputPropsAuditDocuments()} />
								<HStack>
									<SecondaryButton colorScheme="blackAlpha" size={"sm"} onClick={openAuditDocuments}>
										Upload Files
									</SecondaryButton>
									<Text color={isDragActiveAuditDocuments ? "orange.600" : "gray"}>or drag & drop</Text>
								</HStack>
								<Text fontSize="xs" color="gray.500" mt={2}>
									Max file size: {formatFileSize(FILE_SIZE_LIMITS.MAX_FILE_SIZE)} | Max claim size:{" "}
									{formatFileSize(FILE_SIZE_LIMITS.MAX_CLAIM_SIZE)}
								</Text>
							</Stack>
						</Box>
					</div>
				)}
				{showProofOfSchool && (
					<div>
						<Text as="span" fontSize="16" fontWeight={"700"} lineHeight={10}>
							Proof of Full-Time School Enrollment
						</Text>
						<Tooltip
							label="If services were provided for a dependent child, their age is required. Proof of Enrollment as a full-time student MAY be required per the member's policy."
							placement="bottom">
							<span>
								<MdInfoOutline
									style={{ display: "inline-block", fontSize: "16px", verticalAlign: "middle", marginLeft: "8px" }}
								/>
							</span>
						</Tooltip>
						<Box
							{...getRootPropsProofOfFullTimeSchool()}
							backgroundColor={isDragActiveProofOfFullTimeSchool ? "gray.900" : "offWhite"}
							color="gray.1700"
							padding="16px"
							borderColor={isDragActiveProofOfFullTimeSchool ? "gray.800" : "gray.400"}
							borderWidth="1px"
							borderRadius="5px"
							borderStyle="dashed"
							display="flex">
							<Stack rowGap={4} width="100%">
								{renderDocuments(claim.documents, DocumentCategory.PROOF_OF_FULL_TIME_SCHOOL_ENROLLMENT)}
								<input {...getInputPropsProofOfFullTimeSchool()} />
								<HStack>
									<SecondaryButton
										colorScheme="blackAlpha"
										size={"sm"}
										onClick={openProofOfFullTimeSchool}
										isDisabled={isHeldForAudit}>
										Upload Files
									</SecondaryButton>
									<Text color={isDragActiveProofOfFullTimeSchool ? "orange.600" : "gray"}>or drag & drop</Text>
								</HStack>
								<Text fontSize="xs" color="gray.500" mt={2}>
									Max file size: {formatFileSize(FILE_SIZE_LIMITS.MAX_FILE_SIZE)} | Max claim size:{" "}
									{formatFileSize(FILE_SIZE_LIMITS.MAX_CLAIM_SIZE)}
								</Text>
							</Stack>
						</Box>
					</div>
				)}
			</Stack>
			<StepperFooter
				hasPreviousStep={true}
				hasNextStep={true}
				isNextDisabled={isNextDisabled()}
				isSubmit={false}
				onNext={() => handleNextClick()}
				onPrevious={() => handleStepPrevious()}
			/>
		</>
	);
};

export default AssociateDocs;
